import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
  StatTile,
  cn,
  type DataTableCard,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  NEEDS_CALL_LABELS,
  formatMoney,
  formatPercent,
  longDate,
  shortDate,
  toReceivableTableRows,
  useReceivables,
  type ReceivableTableRow,
} from '@/lib/cash-flow';
import { filterRows, sortRows } from '@/lib/facet-table';
import { salesforceOpportunityUrl } from '@/lib/sales';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';

const RECEIVABLE_SORT_FIELDS = {
  event: 'name',
  eventDate: 'eventDate',
  contracted: 'contracted',
  paid: 'paid',
  left: 'left',
  nextDue: 'nextDueDate',
  daysLate: 'daysLate',
  probability: 'collectionProbability',
} as const;
const RECEIVABLE_SORT_KEYS = Object.values(RECEIVABLE_SORT_FIELDS);
const DEFAULT_SORT = { key: RECEIVABLE_SORT_FIELDS.nextDue, direction: 'asc' } as const;
const SEARCH_FIELDS = ['name', 'accountName', 'stageName', 'needsCallText'] as const satisfies ReadonlyArray<keyof ReceivableTableRow>;

/** Which rows the worklist shows (`?show=`). */
const RECEIVABLE_VIEW = {
  all: 'all',
  overdue: 'overdue',
  needsCall: 'needs-call',
} as const;
type ReceivableView = (typeof RECEIVABLE_VIEW)[keyof typeof RECEIVABLE_VIEW];
const VIEW_PARAM = 'show';

function parseView(value: string | null): ReceivableView {
  return value === RECEIVABLE_VIEW.overdue || value === RECEIVABLE_VIEW.needsCall ? value : RECEIVABLE_VIEW.all;
}

const VIEW_FILTERS: Readonly<Record<ReceivableView, (row: ReceivableTableRow) => boolean>> = {
  [RECEIVABLE_VIEW.all]: () => true,
  [RECEIVABLE_VIEW.overdue]: (row) => row.daysLate > 0,
  [RECEIVABLE_VIEW.needsCall]: (row) => row.needsCall,
};

/** Below this the probability reads as a warning. */
const LOW_PROBABILITY = 0.8;

function lateness(daysLate: number): { readonly text: string; readonly late: boolean } {
  if (daysLate > 0) return { text: `${daysLate} ${daysLate === 1 ? 'day' : 'days'} late`, late: true };
  if (daysLate === 0) return { text: 'Due today', late: false };
  const until = -daysLate;
  return { text: `Due in ${until} ${until === 1 ? 'day' : 'days'}`, late: false };
}

function NeedsCall({ row }: { readonly row: ReceivableTableRow }): React.ReactElement {
  if (!row.needsCall) return <span className="text-portal-muted">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {row.needsCallReasons.map((reason) => (
        <Pill key={reason} tone={reason === 'low_probability' ? 'warning' : 'danger'}>
          {NEEDS_CALL_LABELS[reason]}
        </Pill>
      ))}
    </span>
  );
}

function EventName({ row }: { readonly row: ReceivableTableRow }): React.ReactElement {
  return (
    <>
      <a
        href={salesforceOpportunityUrl(row.opportunityId)}
        target="_blank"
        rel="noreferrer"
        className="block text-portal-small font-bold text-brand hover:underline"
      >
        {row.name}
      </a>
      <span className="block text-portal-micro text-portal-muted">{row.accountName}</span>
    </>
  );
}

/**
 * Analytics → Accounting → Receivables: every signed event with money still
 * owed — contracted, paid, left, the next due date, how late it is, how
 * likely the collection curve says it is to arrive, and why it needs a call.
 * The collections worklist. Behind view_accounting.
 */
export function CashReceivablesPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const view = parseView(params.get(VIEW_PARAM));
  const { sort, onSort } = useTableSort(RECEIVABLE_SORT_KEYS, DEFAULT_SORT);
  const { search, setSearch } = useUrlSearch();
  const receivables = useReceivables();

  const allRows = useMemo(() => toReceivableTableRows(receivables.data?.rows ?? []), [receivables.data]);
  const rows = useMemo(
    () => sortRows(filterRows(allRows.filter(VIEW_FILTERS[view]), search, SEARCH_FIELDS), sort),
    [allRows, view, search, sort],
  );
  const counts = useMemo(
    () => ({
      overdue: allRows.filter(VIEW_FILTERS[RECEIVABLE_VIEW.overdue]).length,
      needsCall: allRows.filter(VIEW_FILTERS[RECEIVABLE_VIEW.needsCall]).length,
    }),
    [allRows],
  );

  function setView(next: ReceivableView): void {
    setParams(
      (previous) => {
        const nextParams = new URLSearchParams(previous);
        if (next === RECEIVABLE_VIEW.all) nextParams.delete(VIEW_PARAM);
        else nextParams.set(VIEW_PARAM, next);
        return nextParams;
      },
      { replace: true },
    );
  }

  const cards: DataTableCard[] = rows.map((row) => {
    const late = lateness(row.daysLate);
    return {
      key: row.opportunityId,
      title: row.name,
      subtitle: row.accountName,
      fields: [
        { label: 'Left to collect', value: formatMoney(row.left) },
        { label: 'Next due', value: row.nextDueDate ? shortDate(row.nextDueDate) : '—' },
        { label: 'Lateness', value: late.text },
        { label: 'Likely to collect', value: formatPercent(row.collectionProbability) },
        { label: 'Needs a call', value: row.needsCall ? row.needsCallText : 'No' },
      ],
    };
  });

  const totals = receivables.data?.totals;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ACCOUNTING"
        title="Receivables"
        subtitle={`Signed events with money still owed: what is due, how late it is, and who needs a call.${receivables.data ? ` As of ${longDate(receivables.data.asOf)}.` : ''}`}
      />

      {totals ? (
        <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-5">
          <StatTile label="Contracted" value={formatMoney(totals.contracted)} hint={`${allRows.length} open events`} />
          <StatTile label="Collected" value={formatMoney(totals.paid)} hint="Paid to date on these events" />
          <StatTile label="Left to collect" value={formatMoney(totals.left)} hint={`${totals.needsCall} need a call`} />
          <StatTile label="Overdue" value={formatMoney(totals.overdue)} hint={`${counts.overdue} events past due`} />
          <StatTile
            label="Expected"
            value={formatMoney(totals.expected)}
            hint={`${formatMoney(totals.atRisk)} unlikely to arrive`}
          />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search receivables"
          placeholder="Event, client or reason"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
        <SegmentedControl<ReceivableView>
          label="Which events to show"
          value={view}
          onChange={setView}
          options={[
            { key: RECEIVABLE_VIEW.all, label: `All (${allRows.length})` },
            { key: RECEIVABLE_VIEW.overdue, label: `Overdue (${counts.overdue})` },
            { key: RECEIVABLE_VIEW.needsCall, label: `Needs a call (${counts.needsCall})` },
          ]}
        />
      </div>

      {receivables.isError ? (
        <ErrorNotice title="Receivables unavailable" message={errorMessage(receivables.error)} />
      ) : receivables.isPending ? (
        <SkeletonTable label="Loading receivables…" />
      ) : rows.length === 0 ? (
        <Panel>
          {search.trim() !== '' ? (
            <EmptyState title="No events match" message="Try a different search." />
          ) : allRows.length === 0 ? (
            <EmptyState title="Nothing left to collect" message="Every signed event is paid in full." />
          ) : (
            <EmptyState title="No events in this view" message="Nothing is overdue or flagged for a call right now." />
          )}
        </Panel>
      ) : (
        <DataTable
          headers={[
            { label: 'Event', sortKey: RECEIVABLE_SORT_FIELDS.event },
            { label: 'Event date', sortKey: RECEIVABLE_SORT_FIELDS.eventDate },
            { label: 'Contracted', sortKey: RECEIVABLE_SORT_FIELDS.contracted },
            { label: 'Paid', sortKey: RECEIVABLE_SORT_FIELDS.paid },
            { label: 'Left', sortKey: RECEIVABLE_SORT_FIELDS.left },
            { label: 'Next due', sortKey: RECEIVABLE_SORT_FIELDS.nextDue },
            { label: 'Days late', sortKey: RECEIVABLE_SORT_FIELDS.daysLate },
            { label: 'Likely to collect', sortKey: RECEIVABLE_SORT_FIELDS.probability },
            'Needs a call',
          ]}
          rightAlignedHeaders={[2, 3, 4, 6, 7]}
          sort={sort}
          onSort={onSort}
          minWidth={1240}
          cards={cards}
        >
          {rows.map((row) => {
            const late = lateness(row.daysLate);
            return (
              <tr key={row.opportunityId} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small">
                  <EventName row={row} />
                </td>
                <td className="whitespace-nowrap p-3 text-portal-small text-portal-muted">
                  {row.eventDate ? shortDate(row.eventDate) : '—'}
                </td>
                <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums text-portal-muted">
                  {formatMoney(row.contracted)}
                </td>
                <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums text-portal-muted">
                  {formatMoney(row.paid)}
                </td>
                <td className="whitespace-nowrap p-3 text-right text-portal-small font-bold tabular-nums text-portal-ink">
                  {formatMoney(row.left)}
                </td>
                <td className="whitespace-nowrap p-3 text-portal-small">
                  {row.nextDueDate ? longDate(row.nextDueDate) : '—'}
                </td>
                <td
                  className={cn(
                    'whitespace-nowrap p-3 text-right text-portal-small tabular-nums',
                    late.late ? 'font-bold text-rose-700' : 'text-portal-muted',
                  )}
                >
                  {late.text}
                </td>
                <td
                  className={cn(
                    'whitespace-nowrap p-3 text-right text-portal-small tabular-nums',
                    row.collectionProbability < LOW_PROBABILITY ? 'font-bold text-amber-800' : 'text-portal-muted',
                  )}
                >
                  {formatPercent(row.collectionProbability)}
                </td>
                <td className="min-w-48 p-3 text-portal-small">
                  <NeedsCall row={row} />
                </td>
              </tr>
            );
          })}
        </DataTable>
      )}
    </section>
  );
}
